import axios from 'axios';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ProspectCard from '../CommunityPages/SecretShopper/ProspectCard';
import { Calendar, ChevronDown, Info, Mail, MessageSquareMore, PhoneForwarded, PhoneIncoming, SendHorizonal } from 'lucide-react';
import AudioPlayer from '../CommunityPages/SecretShopper/AudioPlayer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ShadcnStyleLogsPdf from '../CommunityPages/SecretShopper/ShadcnStyleLogsPdf';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { DateTime } from "luxon";
import RenderEmailContent from '../CommunityPages/SecretShopper/RenderEmailContent';
import { CollapsibleTrigger } from '@radix-ui/react-collapsible';
import { useAuth } from '@clerk/clerk-react';

const SecretShopperPublic = () => {
    const { id } = useParams();
    const [secretShopper, setSecretShopper] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const { isSignedIn, isLoaded } = useAuth();

    useEffect(() => {
        const getSecretShopper = async () => {
            try {
                const { data } = await axios.get(`/public-api/secret-shoppers/${id}`);
                setSecretShopper(data);
            } catch (err) {
                setError('Something went wrong.');
                console.error(err);
            } finally {
                setIsLoading(false)
            }
        };

        getSecretShopper();
    }, []);

    if (isLoading) {
        return <>Loading</>
    }

    const getHeader = (log) => {
        if (log.loggable_type === 'SecretShopperPhoneLog') {
            const isOutboundPhoneCall = log.details.from === secretShopper.phone_number;

            return isOutboundPhoneCall ? <><PhoneForwarded className="h-5 w-5 text-gray-500 mr-2" />Secret Shopper To Community </> : <><PhoneIncoming className="h-5 w-5 text-gray-500 mr-2" />{log.details.from} To Secret Shopper</>
        }

        if (log.loggable_type === 'SecretShopperSmsLog') {
            return <><MessageSquareMore className="h-5 w-5 text-gray-500 mr-2" />Text From {log.details.from}</>
        }

        if (log.loggable_type === 'SecretShopperEmailLog') {
            return <div className="flex flex-col gap-1">
                <div className="flex flex-row items-center">
                    <SendHorizonal className="h-5 w-5 text-gray-500 mr-2" />{log.details.sender_email}
                </div>
                <div className="flex flex-row items-center">
                    <Mail className="h-5 w-5 text-gray-500 mr-2" />{log.details.subject}
                </div>
            </div>
        }

        if (log.loggable_type === 'SecretShopperMessageLog') {
            return <><Info className="h-5 w-5 text-gray-500 mr-2" />{log.details.message}</>
        }

        return 'No Message Available';
    }

    if (isLoading) {
        return <>Loading</>
    }

    const getLogBody = (log) => {
        if (log.loggable_type === 'SecretShopperPhoneLog') {
            if (!log?.details.voicemail_recording_signed_url) {
                return 'No Recording Available';
            }

            return <AudioPlayer audioUrl={log.details.voicemail_recording_signed_url} />;
        }

        if (log.loggable_type === 'SecretShopperSmsLog') {
            return log.details.body;
        }

        if (log.loggable_type === 'SecretShopperEmailLog') {
            return <RenderEmailContent html={log.details.body} {...log.details} />
        }

        if (log.loggable_type === 'SecretShopperMessageLog') {
            return <Info className="h-5 w-5 text-gray-500" />
        }

        return 'No additional details available.';
    }


    const formatDate = (dateString) => {
        return DateTime.fromISO(dateString)
            .setZone(secretShopper.community.time_zone)
            .toFormat('MMMM dd, yyyy hh:mm a');
    };

    return (
        <div className="min-h-full min-w-full">
            {!!secretShopper?.id && !error && <div className='grid grid-cols-1 gap-5'>
                {!isSignedIn && isLoaded && <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-100 via-white to-green-100 rounded-md shadow-md w-full max-w-7xl mx-auto mt-10">
                    <div className="flex items-center space-x-4">
                    <img
                        src={`${process.env.PUBLIC_URL}/logo-with-text.png`}
                        alt="Logo"
                        width="100"
                    />
                        <div>
                            <h2 className="text-lg font-semibold">
                                Try Flair’s <span className="bg-gradient-to-r from-transparent via-red-100 to-transparent px-1">Secret Shopping Tool</span>
                            </h2>
                            <p className="text-sm text-gray-600">No Paywall. Just Add a Property.</p>
                        </div>
                    </div>
                    <a href="/signup" className="bg-orange-400 text-white font-semibold px-5 py-2 rounded-md shadow hover:bg-orange-500 transition">
                        Start Shopping
                    </a>
                </div>}
                <ProspectCard
                    secretShopperData={secretShopper}
                    logs={secretShopper.logs}
                    metrics={secretShopper.metrics}
                    community={secretShopper?.community}
                />
                <Card className="w-full max-w-7xl mx-auto shadow-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-semibold flex justify-between">
                            <span>Secret Shopper Activity Log</span>
                            <ShadcnStyleLogsPdf
                                timezone={secretShopper.community.time_zone}
                                secretShopper={secretShopper}
                                community={secretShopper.community}
                                logs={secretShopper.logs}
                            />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {secretShopper.logs.map((log) => (
                                <Collapsible key={log.id} className="bg-gray-50 p-4 rounded-lg space-y-2">
                                    <div className="flex flex-row justify-between">
                                        <div className="flex flex-col">
                                            <div className="flex items-center space-x-2 mb-2">
                                                {getHeader(log)}
                                            </div>
                                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                                                <Calendar className="h-4 w-4" />
                                                <span>{formatDate(log.created_at)}</span>
                                            </div>
                                        </div>
                                        <CollapsibleTrigger className="flex items-center text-black space-x-1 cursor-pointer">
                                            <ChevronDown className="h-6 w-6" />
                                        </CollapsibleTrigger>
                                    </div>
                                    <CollapsibleContent className="pl-6 pt-2">
                                        <div className="text-sm text-gray-700">
                                            {getLogBody(log)}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>}

            {!!error && <p className="text-red-600 w-full text-center">{error}</p>}
        </div>
    );
};

export default SecretShopperPublic;
